// Medical Scores Database - Complete Collection Part 11
// Additional scores from medicalcul.free.fr - A-B section

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  User,
  Beaker
} from 'lucide-react';

export const SCORES_PART_11 = {
  // ==================== SCORES A ====================
  adiva: {
    name: 'A-DIVA (Difficulté perfusion adulte)',
    category: 'Soins infirmiers',
    icon: Syringe,
    description: 'Prédiction difficulté à perfuser un adulte',
    color: 'blue',
    fields: [
      { id: 'history', label: 'Antécédent de perfusion difficile', type: 'checkbox', points: 1 },
      { id: 'notVisible', label: 'Veines non visibles après garrot', type: 'checkbox', points: 1 },
      { id: 'notPalpable', label: 'Veines non palpables après garrot', type: 'checkbox', points: 1 },
      { id: 'diameter', label: 'Diamètre veine estimé < 3mm', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.history) total += 1;
      if (values.notVisible) total += 1;
      if (values.notPalpable) total += 1;
      if (values.diameter) total += 1;
      return { score: total, max: 4 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `A-DIVA ${score}/4`, detail: 'Faible risque de difficulté. Technique standard.' };
      if (score <= 2) return { level: 'warning', text: `A-DIVA ${score}/4`, detail: 'Risque modéré. Prévoir matériel adapté.' };
      return { level: 'danger', text: `A-DIVA ${score}/4`, detail: 'Haut risque. Envisager échoguidage ou opérateur expérimenté.' };
    },
    formula: 'Score A-DIVA ≥ 3 = difficulté anticipée'
  },

  abcTransfusion: {
    name: 'ABC Transfusion',
    category: 'Urgences',
    icon: Droplets,
    description: 'Besoins transfusion massive sur traumatisme',
    color: 'red',
    fields: [
      { id: 'penetrating', label: 'Mécanisme pénétrant', type: 'checkbox', points: 1 },
      { id: 'sbp', label: 'PAS ≤ 90 mmHg aux urgences', type: 'checkbox', points: 1 },
      { id: 'hr', label: 'FC ≥ 120/min aux urgences', type: 'checkbox', points: 1 },
      { id: 'fast', label: 'FAST positif (épanchement)', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.penetrating) total += 1;
      if (values.sbp) total += 1;
      if (values.hr) total += 1;
      if (values.fast) total += 1;
      return { score: total, max: 4 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `ABC ${score}/4`, detail: 'Faible probabilité de transfusion massive.' };
      return { level: 'danger', text: `ABC ${score}/4`, detail: 'Haute probabilité de transfusion massive. Activer protocole.' };
    },
    formula: 'Score ABC ≥ 2 = transfusion massive probable (Se 75%, Sp 86%)'
  },

  abcd2: {
    name: 'ABCD² (Post-AIT)',
    category: 'Neurologie',
    icon: Brain,
    description: 'Risque AVC après accident ischémique transitoire',
    color: 'purple',
    fields: [
      { id: 'age', label: 'Âge ≥ 60 ans', type: 'checkbox', points: 1 },
      { id: 'bp', label: 'PA ≥ 140/90 mmHg', type: 'checkbox', points: 1 },
      { id: 'clinicalUnilateral', label: 'Déficit moteur unilatéral', type: 'checkbox', points: 2 },
      { id: 'clinicalSpeech', label: 'Troubles du langage sans déficit moteur', type: 'checkbox', points: 1 },
      { id: 'duration60', label: 'Durée ≥ 60 min', type: 'checkbox', points: 2 },
      { id: 'duration10', label: 'Durée 10-59 min', type: 'checkbox', points: 1 },
      { id: 'diabetes', label: 'Diabète', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.age) total += 1;
      if (values.bp) total += 1;
      if (values.clinicalUnilateral) total += 2;
      else if (values.clinicalSpeech) total += 1;
      if (values.duration60) total += 2;
      else if (values.duration10) total += 1;
      if (values.diabetes) total += 1;
      return { score: total, max: 7 };
    },
    interpret: (score) => {
      if (score <= 3) return { level: 'success', text: `ABCD² ${score}/7`, detail: 'Risque faible AVC à 2j: 1%. Surveillance ambulatoire possible.' };
      if (score <= 5) return { level: 'warning', text: `ABCD² ${score}/7`, detail: 'Risque modéré AVC à 2j: 4%. Hospitalisation recommandée.' };
      return { level: 'danger', text: `ABCD² ${score}/7`, detail: 'Risque élevé AVC à 2j: 8%. Hospitalisation urgente + bilan complet.' };
    },
    formula: 'ABCD² = Age + Blood pressure + Clinical + Duration + Diabetes'
  },

  absi: {
    name: 'ABSI (Abbreviated Burn Severity Index)',
    category: 'Brûlés',
    icon: Flame,
    description: 'Probabilité survie patient brûlé',
    color: 'orange',
    fields: [
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 1, label: '0-20 ans' },
        { value: 2, label: '21-40 ans' },
        { value: 3, label: '41-60 ans' },
        { value: 4, label: '61-80 ans' },
        { value: 5, label: '> 80 ans' }
      ]},
      { id: 'scb', label: 'Surface corporelle brûlée', type: 'select', options: [
        { value: 1, label: '1-10%' },
        { value: 2, label: '11-20%' },
        { value: 3, label: '21-30%' },
        { value: 4, label: '31-40%' },
        { value: 5, label: '41-50%' },
        { value: 6, label: '51-60%' },
        { value: 7, label: '61-70%' },
        { value: 8, label: '71-80%' },
        { value: 9, label: '81-90%' },
        { value: 10, label: '> 90%' }
      ]},
      { id: 'inhalation', label: 'Inhalation de fumées', type: 'checkbox', points: 1 },
      { id: 'deep', label: 'Brûlure profonde (3e degré)', type: 'checkbox', points: 1 },
      { id: 'female', label: 'Sexe féminin', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = (values.age || 0) + (values.scb || 0);
      if (values.inhalation) total += 1;
      if (values.deep) total += 1;
      if (values.female) total += 1;
      return { score: total, max: 18 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: `ABSI ${score}`, detail: 'Survie > 99%' };
      if (score <= 6) return { level: 'success', text: `ABSI ${score}`, detail: 'Survie 90-99%' };
      if (score <= 8) return { level: 'warning', text: `ABSI ${score}`, detail: 'Survie 50-90%' };
      if (score <= 10) return { level: 'danger', text: `ABSI ${score}`, detail: 'Survie 20-50%' };
      return { level: 'danger', text: `ABSI ${score}`, detail: 'Survie < 20%' };
    },
    formula: 'ABSI = Age + SCB + Inhalation + Profondeur + Sexe'
  },

  albi: {
    name: 'ALBI (Albumin-Bilirubin)',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Évaluation fonction hépatique',
    color: 'yellow',
    fields: [
      { id: 'albumin', label: 'Albumine (g/L)', type: 'number', min: 10, max: 60 },
      { id: 'bilirubin', label: 'Bilirubine totale (µmol/L)', type: 'number', min: 1, max: 500 }
    ],
    calculate: (values) => {
      const alb = values.albumin || 35;
      const bil = values.bilirubin || 17;
      const albi = (Math.log10(bil) * 0.66) + (alb * -0.085);
      return { score: Math.round(albi * 100) / 100, max: 'variable' };
    },
    interpret: (score) => {
      if (score <= -2.60) return { level: 'success', text: `ALBI Grade 1`, detail: 'Bonne fonction hépatique. Médiane survie > 24 mois.' };
      if (score <= -1.39) return { level: 'warning', text: `ALBI Grade 2`, detail: 'Fonction hépatique modérée. Médiane survie 12-24 mois.' };
      return { level: 'danger', text: `ALBI Grade 3`, detail: 'Fonction hépatique sévèrement altérée. Médiane survie < 12 mois.' };
    },
    formula: 'ALBI = (log10(Bil) × 0.66) + (Alb × -0.085). Grade 1: ≤-2.60, Grade 2: -2.60 à -1.39, Grade 3: >-1.39'
  },

  algoplus: {
    name: 'Algoplus (Douleur personne âgée)',
    category: 'Gériatrie',
    icon: User,
    description: 'Évaluation douleur de la personne âgée',
    color: 'violet',
    fields: [
      { id: 'face', label: 'Visage: froncement sourcils, grimaces, crispation', type: 'checkbox', points: 1 },
      { id: 'regard', label: 'Regard: inattentif, fixe, lointain, pleurs', type: 'checkbox', points: 1 },
      { id: 'plaintes', label: 'Plaintes: cris, gémissements, soupirs', type: 'checkbox', points: 1 },
      { id: 'corps', label: 'Corps: retrait, protection zone, immobilité', type: 'checkbox', points: 1 },
      { id: 'comportement', label: 'Comportement: agitation, agressivité, agrippement', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.face) total += 1;
      if (values.regard) total += 1;
      if (values.plaintes) total += 1;
      if (values.corps) total += 1;
      if (values.comportement) total += 1;
      return { score: total, max: 5 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `Algoplus ${score}/5`, detail: 'Pas de douleur évidente. Surveillance.' };
      return { level: 'danger', text: `Algoplus ${score}/5`, detail: 'Douleur présente (seuil ≥2). Traitement antalgique nécessaire.' };
    },
    formula: 'Score ≥ 2 = douleur présente nécessitant traitement'
  },

  altemeier: {
    name: 'Altemeier (Risque infectieux chirurgical)',
    category: 'Infectiologie',
    icon: Thermometer,
    description: 'Classification risque infectieux intervention chirurgicale',
    color: 'rose',
    fields: [
      { id: 'class', label: 'Type de chirurgie', type: 'select', options: [
        { value: 1, label: 'Classe I - Propre (infection < 2%)' },
        { value: 2, label: 'Classe II - Propre-contaminée (infection 10%)' },
        { value: 3, label: 'Classe III - Contaminée (infection 20%)' },
        { value: 4, label: 'Classe IV - Sale-infectée (infection 40%)' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.class || 1, max: 4 };
    },
    interpret: (score) => {
      if (score === 1) return { level: 'success', text: 'Classe I - Propre', detail: 'Pas de rupture technique aseptique, pas d\'inflammation, pas d\'ouverture tractus. Risque infection < 2%.' };
      if (score === 2) return { level: 'success', text: 'Classe II - Propre-contaminée', detail: 'Ouverture tractus contrôlée sans contamination. Risque infection ~10%.' };
      if (score === 3) return { level: 'warning', text: 'Classe III - Contaminée', detail: 'Rupture technique ou contamination tractus. Risque infection ~20%.' };
      return { level: 'danger', text: 'Classe IV - Sale-infectée', detail: 'Infection préexistante ou perforation. Risque infection ~40%. Antibioprophylaxie indispensable.' };
    },
    formula: 'Classification d\'Altemeier pour antibioprophylaxie chirurgicale'
  },

  // ==================== SCORES B ====================
  bangui: {
    name: 'Bangui (Définition SIDA)',
    category: 'Infectiologie',
    icon: Thermometer,
    description: 'Définition clinique du SIDA (pays sans sérologie)',
    color: 'rose',
    fields: [
      { id: 'weightLoss', label: 'Amaigrissement > 10%', type: 'checkbox', points: 2 },
      { id: 'diarrhea', label: 'Diarrhée > 1 mois', type: 'checkbox', points: 2 },
      { id: 'fever', label: 'Fièvre > 1 mois', type: 'checkbox', points: 2 },
      { id: 'cough', label: 'Toux persistante > 1 mois', type: 'checkbox', points: 1 },
      { id: 'dermatitis', label: 'Dermite prurigineuse généralisée', type: 'checkbox', points: 1 },
      { id: 'herpes', label: 'Herpès récidivant', type: 'checkbox', points: 1 },
      { id: 'candida', label: 'Candidose oropharyngée', type: 'checkbox', points: 1 },
      { id: 'lymph', label: 'Adénopathies généralisées', type: 'checkbox', points: 1 },
      { id: 'kaposi', label: 'Sarcome de Kaposi', type: 'checkbox', points: 4 },
      { id: 'meningitis', label: 'Méningite à cryptocoque', type: 'checkbox', points: 4 }
    ],
    calculate: (values) => {
      let major = 0;
      let minor = 0;
      if (values.weightLoss) major += 1;
      if (values.diarrhea) major += 1;
      if (values.fever) major += 1;
      if (values.cough) minor += 1;
      if (values.dermatitis) minor += 1;
      if (values.herpes) minor += 1;
      if (values.candida) minor += 1;
      if (values.lymph) minor += 1;
      if (values.kaposi || values.meningitis) return { score: 'SIDA', max: 'Critère pathognomonique' };
      return { score: `${major} majeur(s), ${minor} mineur(s)`, max: '3 majeurs + 5 mineurs' };
    },
    interpret: (score, calculated) => {
      if (score === 'SIDA') return { level: 'danger', text: 'SIDA', detail: 'Présence d\'un critère pathognomonique (Kaposi ou méningite cryptococcique).' };
      const parts = score.split(',');
      const major = parseInt(parts[0]) || 0;
      const minor = parseInt(parts[1]) || 0;
      if (major >= 2 && minor >= 1) return { level: 'danger', text: 'SIDA probable', detail: '≥2 critères majeurs ET ≥1 mineur = définition OMS du SIDA.' };
      return { level: 'warning', text: 'Critères insuffisants', detail: 'Ne remplit pas les critères de Bangui. Poursuite du bilan.' };
    },
    formula: 'SIDA = ≥2 signes majeurs + ≥1 signe mineur OU 1 signe pathognomonique'
  },

  binet: {
    name: 'Binet (LLC)',
    category: 'Hématologie',
    icon: Droplets,
    description: 'Classification leucémie lymphoïde chronique',
    color: 'red',
    fields: [
      { id: 'areas', label: 'Nombre d\'aires ganglionnaires atteintes', type: 'select', options: [
        { value: 0, label: '0 aire' },
        { value: 1, label: '1 aire' },
        { value: 2, label: '2 aires' },
        { value: 3, label: '3 aires' },
        { value: 4, label: '4 aires' },
        { value: 5, label: '5 aires (cervical, axillaire, inguinal, rate, foie)' }
      ]},
      { id: 'hb', label: 'Hémoglobine (g/dL)', type: 'number', min: 5, max: 20 },
      { id: 'platelets', label: 'Plaquettes (G/L)', type: 'number', min: 10, max: 500 }
    ],
    calculate: (values) => {
      const areas = values.areas || 0;
      const hb = values.hb || 12;
      const plat = values.platelets || 150;
      
      if (hb < 10 || plat < 100) return { score: 'C', max: 'Stade C' };
      if (areas >= 3) return { score: 'B', max: 'Stade B' };
      return { score: 'A', max: 'Stade A' };
    },
    interpret: (score) => {
      if (score === 'A') return { level: 'success', text: 'Binet A', detail: 'Stade A: < 3 aires atteintes, Hb et plaq normales. Médiane survie > 10 ans. Abstention thérapeutique.' };
      if (score === 'B') return { level: 'warning', text: 'Binet B', detail: 'Stade B: ≥ 3 aires atteintes, Hb et plaq normales. Médiane survie 5-7 ans. Surveillance rapprochée.' };
      return { level: 'danger', text: 'Binet C', detail: 'Stade C: Anémie (Hb < 10) et/ou thrombopénie (< 100G/L). Médiane survie 2-3 ans. Traitement indiqué.' };
    },
    formula: 'Stade A: < 3 aires, Hb/plaq N. Stade B: ≥ 3 aires, Hb/plaq N. Stade C: Hb < 10 ou plaq < 100'
  },

  bishop: {
    name: 'Bishop (Maturation cervicale)',
    category: 'Obstétrique',
    icon: Baby,
    description: 'Pronostic accouchement - maturité cervicale',
    color: 'pink',
    fields: [
      { id: 'dilation', label: 'Dilatation col (cm)', type: 'select', options: [
        { value: 0, label: '0 - Fermé' },
        { value: 1, label: '1 - 1-2 cm' },
        { value: 2, label: '2 - 3-4 cm' },
        { value: 3, label: '3 - ≥ 5 cm' }
      ]},
      { id: 'effacement', label: 'Effacement (%)', type: 'select', options: [
        { value: 0, label: '0 - 0-30%' },
        { value: 1, label: '1 - 40-50%' },
        { value: 2, label: '2 - 60-70%' },
        { value: 3, label: '3 - ≥ 80%' }
      ]},
      { id: 'station', label: 'Hauteur présentation', type: 'select', options: [
        { value: 0, label: '0 - -3' },
        { value: 1, label: '1 - -2' },
        { value: 2, label: '2 - -1 ou 0' },
        { value: 3, label: '3 - +1 ou +2' }
      ]},
      { id: 'consistency', label: 'Consistance col', type: 'select', options: [
        { value: 0, label: '0 - Ferme' },
        { value: 1, label: '1 - Moyen' },
        { value: 2, label: '2 - Mou' }
      ]},
      { id: 'position', label: 'Position col', type: 'select', options: [
        { value: 0, label: '0 - Postérieur' },
        { value: 1, label: '1 - Intermédiaire' },
        { value: 2, label: '2 - Antérieur' }
      ]}
    ],
    calculate: (values) => {
      const total = (values.dilation || 0) + (values.effacement || 0) + 
                    (values.station || 0) + (values.consistency || 0) + (values.position || 0);
      return { score: total, max: 13 };
    },
    interpret: (score) => {
      if (score <= 5) return { level: 'warning', text: `Bishop ${score}/13`, detail: 'Col défavorable. Maturation cervicale recommandée avant déclenchement.' };
      if (score <= 8) return { level: 'success', text: `Bishop ${score}/13`, detail: 'Col intermédiaire. Déclenchement possible.' };
      return { level: 'success', text: `Bishop ${score}/13`, detail: 'Col favorable. Bon pronostic d\'accouchement voie basse.' };
    },
    formula: 'Bishop ≤ 5: col défavorable. Bishop 6-8: intermédiaire. Bishop ≥ 9: favorable'
  },

  blatchford: {
    name: 'Blatchford (Hémorragie digestive)',
    category: 'Hépato-Gastro',
    icon: Droplets,
    description: 'Risque récidive hémorragie digestive haute',
    color: 'red',
    fields: [
      { id: 'urea', label: 'Urée sanguine (mmol/L)', type: 'select', options: [
        { value: 0, label: '< 6.5' },
        { value: 2, label: '6.5-7.9' },
        { value: 3, label: '8.0-9.9' },
        { value: 4, label: '10.0-24.9' },
        { value: 6, label: '≥ 25' }
      ]},
      { id: 'hbMale', label: 'Hémoglobine homme (g/dL)', type: 'select', options: [
        { value: 0, label: '≥ 13' },
        { value: 1, label: '12.0-12.9' },
        { value: 3, label: '10.0-11.9' },
        { value: 6, label: '< 10' }
      ]},
      { id: 'sbp', label: 'PAS (mmHg)', type: 'select', options: [
        { value: 0, label: '≥ 110' },
        { value: 1, label: '100-109' },
        { value: 2, label: '90-99' },
        { value: 3, label: '< 90' }
      ]},
      { id: 'pulse', label: 'Pouls ≥ 100/min', type: 'checkbox', points: 1 },
      { id: 'melena', label: 'Méléna', type: 'checkbox', points: 1 },
      { id: 'syncope', label: 'Syncope', type: 'checkbox', points: 2 },
      { id: 'liver', label: 'Maladie hépatique', type: 'checkbox', points: 2 },
      { id: 'heart', label: 'Insuffisance cardiaque', type: 'checkbox', points: 2 }
    ],
    calculate: (values) => {
      let total = (values.urea || 0) + (values.hbMale || 0) + (values.sbp || 0);
      if (values.pulse) total += 1;
      if (values.melena) total += 1;
      if (values.syncope) total += 2;
      if (values.liver) total += 2;
      if (values.heart) total += 2;
      return { score: total, max: 23 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: `Blatchford ${score}`, detail: 'Risque très faible. Prise en charge ambulatoire possible.' };
      if (score <= 3) return { level: 'warning', text: `Blatchford ${score}`, detail: 'Risque faible. Hospitalisation courte, endoscopie différable.' };
      return { level: 'danger', text: `Blatchford ${score}`, detail: 'Risque élevé. Hospitalisation, endoscopie urgente, réanimation si besoin.' };
    },
    formula: 'Score 0 = très faible risque, prise en charge ambulatoire possible'
  },

  braden: {
    name: 'Braden (Risque escarre)',
    category: 'Dermatologie',
    icon: User,
    description: 'Évaluation risque d\'escarre',
    color: 'orange',
    fields: [
      { id: 'sensory', label: 'Perception sensorielle', type: 'select', options: [
        { value: 1, label: '1 - Complètement limitée' },
        { value: 2, label: '2 - Très limitée' },
        { value: 3, label: '3 - Légèrement diminuée' },
        { value: 4, label: '4 - Aucune diminution' }
      ]},
      { id: 'moisture', label: 'Humidité', type: 'select', options: [
        { value: 1, label: '1 - Constamment humide' },
        { value: 2, label: '2 - Très humide' },
        { value: 3, label: '3 - Occasionnellement humide' },
        { value: 4, label: '4 - Rarement humide' }
      ]},
      { id: 'activity', label: 'Activité', type: 'select', options: [
        { value: 1, label: '1 - Alité' },
        { value: 2, label: '2 - Confiné au fauteuil' },
        { value: 3, label: '3 - Marche occasionnellement' },
        { value: 4, label: '4 - Marche fréquemment' }
      ]},
      { id: 'mobility', label: 'Mobilité', type: 'select', options: [
        { value: 1, label: '1 - Complètement immobile' },
        { value: 2, label: '2 - Très limitée' },
        { value: 3, label: '3 - Légèrement diminuée' },
        { value: 4, label: '4 - Aucune limitation' }
      ]},
      { id: 'nutrition', label: 'Nutrition', type: 'select', options: [
        { value: 1, label: '1 - Très pauvre' },
        { value: 2, label: '2 - Probablement inadéquate' },
        { value: 3, label: '3 - Adéquate' },
        { value: 4, label: '4 - Excellente' }
      ]},
      { id: 'friction', label: 'Friction et cisaillement', type: 'select', options: [
        { value: 1, label: '1 - Problème' },
        { value: 2, label: '2 - Problème potentiel' },
        { value: 3, label: '3 - Aucun problème apparent' }
      ]}
    ],
    calculate: (values) => {
      const total = (values.sensory || 4) + (values.moisture || 4) + (values.activity || 4) +
                    (values.mobility || 4) + (values.nutrition || 4) + (values.friction || 3);
      return { score: total, max: 23 };
    },
    interpret: (score) => {
      if (score <= 9) return { level: 'danger', text: `Braden ${score}/23`, detail: 'Risque très élevé d\'escarre. Prévention intensive requise.' };
      if (score <= 12) return { level: 'danger', text: `Braden ${score}/23`, detail: 'Risque élevé d\'escarre. Mobilisation, matelas adapté, surveillance.' };
      if (score <= 14) return { level: 'warning', text: `Braden ${score}/23`, detail: 'Risque modéré. Prévention standard recommandée.' };
      if (score <= 18) return { level: 'success', text: `Braden ${score}/23`, detail: 'Risque faible. Surveillance standard.' };
      return { level: 'success', text: `Braden ${score}/23`, detail: 'Pas de risque particulier.' };
    },
    formula: 'Score ≤ 12: risque élevé. Score 13-14: modéré. Score ≥ 15: faible'
  },

  bref: {
    name: 'BREF (Batterie Rapide Efficience Frontale)',
    category: 'Neurologie',
    icon: Brain,
    description: 'Évaluation rapide fonctions cognitives frontales',
    color: 'purple',
    fields: [
      { id: 'similarities', label: 'Similitudes (0-3)', type: 'select', options: [
        { value: 0, label: '0 - Aucune réponse correcte' },
        { value: 1, label: '1 - Une catégorie' },
        { value: 2, label: '2 - Deux catégories' },
        { value: 3, label: '3 - Trois catégories' }
      ]},
      { id: 'fluency', label: 'Fluence lexicale (mots en S)', type: 'select', options: [
        { value: 0, label: '0 - Moins de 3 mots' },
        { value: 1, label: '1 - 3-5 mots' },
        { value: 2, label: '2 - 6-9 mots' },
        { value: 3, label: '3 - Plus de 9 mots' }
      ]},
      { id: 'motor', label: 'Séquences motrices (Luria)', type: 'select', options: [
        { value: 0, label: '0 - Échec' },
        { value: 1, label: '1 - Avec l\'examinateur' },
        { value: 2, label: '2 - Seul > 3 essais' },
        { value: 3, label: '3 - Seul ≤ 3 essais' }
      ]},
      { id: 'conflicting', label: 'Consignes conflictuelles', type: 'select', options: [
        { value: 0, label: '0 - Tapé comme examinateur' },
        { value: 1, label: '1 - > 2 erreurs' },
        { value: 2, label: '2 - 1-2 erreurs' },
        { value: 3, label: '3 - Aucune erreur' }
      ]},
      { id: 'goNoGo', label: 'Go-No Go', type: 'select', options: [
        { value: 0, label: '0 - Tapé comme examinateur' },
        { value: 1, label: '1 - > 2 erreurs' },
        { value: 2, label: '2 - 1-2 erreurs' },
        { value: 3, label: '3 - Aucune erreur' }
      ]},
      { id: 'prehension', label: 'Comportement de préhension', type: 'select', options: [
        { value: 0, label: '0 - Prend les mains' },
        { value: 1, label: '1 - Hésite ou demande' },
        { value: 2, label: '2 - Hésite puis s\'abstient' },
        { value: 3, label: '3 - Ne touche pas' }
      ]}
    ],
    calculate: (values) => {
      const total = (values.similarities || 0) + (values.fluency || 0) + (values.motor || 0) +
                    (values.conflicting || 0) + (values.goNoGo || 0) + (values.prehension || 0);
      return { score: total, max: 18 };
    },
    interpret: (score) => {
      if (score >= 16) return { level: 'success', text: `BREF ${score}/18`, detail: 'Fonctions frontales normales.' };
      if (score >= 12) return { level: 'warning', text: `BREF ${score}/18`, detail: 'Atteinte frontale légère à modérée.' };
      return { level: 'danger', text: `BREF ${score}/18`, detail: 'Atteinte frontale sévère. Bilan neuropsychologique complet recommandé.' };
    },
    formula: 'BREF < 16: atteinte frontale probable. < 12: sévère'
  },

  burchWartofsky: {
    name: 'Burch-Wartofsky (Crise thyréotoxique)',
    category: 'Médecine interne',
    icon: Thermometer,
    description: 'Diagnostic crise thyréotoxique',
    color: 'orange',
    fields: [
      { id: 'temp', label: 'Température', type: 'select', options: [
        { value: 0, label: '< 37.2°C' },
        { value: 5, label: '37.2-37.7°C' },
        { value: 10, label: '37.8-38.2°C' },
        { value: 15, label: '38.3-38.8°C' },
        { value: 20, label: '38.9-39.4°C' },
        { value: 25, label: '39.5-39.9°C' },
        { value: 30, label: '≥ 40°C' }
      ]},
      { id: 'cns', label: 'Atteinte SNC', type: 'select', options: [
        { value: 0, label: 'Absente' },
        { value: 10, label: 'Agitation légère' },
        { value: 20, label: 'Delirium, psychose, léthargie extrême' },
        { value: 30, label: 'Convulsions ou coma' }
      ]},
      { id: 'gi', label: 'Dysfonction gastro-intestinale', type: 'select', options: [
        { value: 0, label: 'Absente' },
        { value: 10, label: 'Modérée (diarrhée, nausées, douleurs)' },
        { value: 20, label: 'Sévère (ictère inexpliqué)' }
      ]},
      { id: 'hr', label: 'Fréquence cardiaque', type: 'select', options: [
        { value: 0, label: '< 90/min' },
        { value: 5, label: '90-109/min' },
        { value: 10, label: '110-119/min' },
        { value: 15, label: '120-129/min' },
        { value: 20, label: '130-139/min' },
        { value: 25, label: '≥ 140/min' }
      ]},
      { id: 'chf', label: 'Insuffisance cardiaque', type: 'select', options: [
        { value: 0, label: 'Absente' },
        { value: 5, label: 'Légère (œdèmes)' },
        { value: 10, label: 'Modérée (crépitants)' },
        { value: 15, label: 'Sévère (OAP)' }
      ]},
      { id: 'af', label: 'Fibrillation auriculaire', type: 'checkbox', points: 10 },
      { id: 'precipitant', label: 'Facteur déclenchant identifié', type: 'select', options: [
        { value: 0, label: 'Présent' },
        { value: 10, label: 'Absent' }
      ]}
    ],
    calculate: (values) => {
      let total = (values.temp || 0) + (values.cns || 0) + (values.gi || 0) + 
                  (values.hr || 0) + (values.chf || 0) + (values.precipitant || 0);
      if (values.af) total += 10;
      return { score: total, max: 140 };
    },
    interpret: (score) => {
      if (score < 25) return { level: 'success', text: `Burch-Wartofsky ${score}`, detail: 'Crise thyréotoxique improbable.' };
      if (score < 45) return { level: 'warning', text: `Burch-Wartofsky ${score}`, detail: 'Crise thyréotoxique possible. Surveillance rapprochée.' };
      return { level: 'danger', text: `Burch-Wartofsky ${score}`, detail: 'Crise thyréotoxique hautement probable. Traitement urgent.' };
    },
    formula: '< 25: improbable. 25-44: possible. ≥ 45: hautement probable'
  }
};
